import {Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef, ViewChild} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {RealEstate, RealEstateCards} from '../../interfaces/real-estate';
import {RealEstateService} from '../../services/real-estate/real-estate.service';
import {AsyncPipe, CurrencyPipe, DatePipe, DecimalPipe, JsonPipe, KeyValuePipe} from '@angular/common';
import {FavoritesService} from '../../services/favorites/favorites.service';
import {interval, Observable, Subject, takeUntil, takeWhile, timer} from 'rxjs';
import {initFlowbite} from 'flowbite';
import {GoogleMap, MapMarker} from '@angular/google-maps';
import {FinancialReportComponent} from '../financial-report/financial-report.component';
import {ReportService} from '../../services/report/report.service';
import {CostReport} from '../../interfaces/cost-report';
import {Base64Helper} from '../../utils/base64-helper';
import {FavoriteAlertConsentModalComponent} from '../favorite-alert-consent-modal/favorite-alert-consent-modal.component';
import {MarketingConsentService} from '../../services/marketing-consent/marketing-consent.service';
import {AuthenticationService} from '../../services/authentication/authentication.service';
import {RealEstateNearbyPlacesComponent} from '../real-estate-nearby-places/real-estate-nearby-places.component';
import {MissionTemplateComponent} from '../mission-template/mission-template.component';
import {WhyChooseTemplateComponent} from '../why-choose-template/why-choose-template.component';

interface AnalysisMapPoint {
  title: string;
  label: string;
  coordinates: {
    latOffset: number;
    lngOffset: number;
  };
}

interface RiskIndicator {
  title: string;
  status: string;
  description: string;
  recommendation?: string;
}

interface ValidationChecklistItem {
  id: string;
  label: string;
}

interface EstimatedCost {
  title: string;
  description: string;
}

interface RiskRecommendation {
  title: string;
  description: string;
}

@Component({
  selector: 'app-real-estate-details',
  imports: [
    AsyncPipe,
    KeyValuePipe,
    CurrencyPipe,
    DatePipe,
    JsonPipe,
    DecimalPipe,
    GoogleMap,
    MapMarker,
    CommonModule,
    FormsModule,
    RouterLink,
    FinancialReportComponent,
    FavoriteAlertConsentModalComponent,
    RealEstateNearbyPlacesComponent,
    MissionTemplateComponent,
    WhyChooseTemplateComponent
  ],
  templateUrl: './real-estate-details.component.html',
  styleUrl: './real-estate-details.component.css'
})
export class RealEstateDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  realEstate: RealEstate | undefined = undefined;
  isLoading: boolean = false;
  activeDetailsTab = 'official-auction-information';
  readonly detailsTabs = [
    { id: 'official-auction-information', label: 'Informações Oficiais do Leilão', buttonId: 'profile-styled-tab' },
    { id: 'cost-analysis', label: 'Simulação de Custos', buttonId: 'settings-styled-tab' },
    { id: 'neighborhood-analysis', label: 'Análise do Entorno', buttonId: 'neighborhood-styled-tab' },
    { id: 'validation-risks', label: 'Validação e riscos', buttonId: 'validation-risks-styled-tab' }
  ];
  favorites: Observable<string[]>;
  mapGeoLocation: { lat: number, lng: number } | undefined = undefined;
  @ViewChild('neighborhoodMap') neighborhoodMap?: GoogleMap;
  @ViewChild(RealEstateNearbyPlacesComponent) nearbyPlacesComponent?: RealEstateNearbyPlacesComponent;

  costReport: CostReport | undefined = undefined;
  pendingFavoriteRealEstateId: string | null = null;
  showFavoriteAlertConsentModal = false;
  showAuthChoiceModal = false;
  isSavingFavoriteAlertConsent = false;
  favoriteAlertConsentError = '';
  readonly analysisMapPoints: AnalysisMapPoint[] = [
    { title: 'Mobilidade: acessos e transporte', label: 'M', coordinates: { latOffset: -0.004, lngOffset: 0.006 } },
    { title: 'Serviços: comércio e rotina', label: 'S', coordinates: { latOffset: 0.005, lngOffset: 0.004 } },
    { title: 'Liquidez: sinais de demanda', label: 'L', coordinates: { latOffset: -0.006, lngOffset: -0.005 } },
    { title: 'Atenção: validar presencialmente', label: 'A', coordinates: { latOffset: 0.006, lngOffset: -0.006 } }
  ];
  readonly mobilityInsights = [
    { label: 'Transporte público', value: 'Pontos e estações próximas', detail: 'Verificar frequência, linhas disponíveis e horários de pico.' },
    { label: 'Vias principais', value: 'Acessos relevantes no entorno', detail: 'Confirmar fluxo de veículos e facilidade de entrada/saída.' },
    { label: 'Centro da região', value: 'Deslocamento estimado variável', detail: 'Avaliar o trajeto em dias úteis e finais de semana.' },
    { label: 'Conexões intermunicipais', value: 'Dependente da cidade', detail: 'Validar rodoviária, aeroporto ou eixos regionais quando aplicável.' }
  ];
  readonly attentionChecks = [
    'Ruído em diferentes horários',
    'Iluminação e conservação da rua',
    'Segurança percebida no entorno',
    'Fluxo de veículos e facilidade de estacionamento',
    'Condição das calçadas e acessibilidade',
    'Demanda local para moradia, locação ou revenda'
  ];
  readonly nearbyComparableSignals = [
    { type: 'Apartamento', range: 'Faixa estimada por m²', note: 'Comparar com metragem, conservação e documentação.' },
    { type: 'Casa', range: 'Faixa estimada por padrão construtivo', note: 'Amostras próximas podem variar bastante por rua.' },
    { type: 'Leilões recentes', range: 'Referência histórica aproximada', note: 'Confirmar edital, ocupação e condições de pagamento.' }
  ];
  readonly demandSignals = [
    { label: 'Moradia', description: 'Serviços essenciais e transporte tendem a ampliar o público potencial.' },
    { label: 'Locação', description: 'Proximidade de comércio, educação e saúde pode apoiar análise de demanda.' },
    { label: 'Revenda', description: 'Liquidez depende de preço, documentação, estado do imóvel e dinâmica local.' }
  ];
  readonly riskIndicators: RiskIndicator[] = [
    {
      title: 'Ocupação',
      status: 'Requer validação',
      description: 'Existem sinais que podem indicar ocupação ou necessidade de confirmação presencial.',
      recommendation: 'Validar presencialmente e ler o edital completo.'
    },
    {
      title: 'Situação documental',
      status: 'Não confirmado',
      description: 'A leitura inicial depende dos documentos disponíveis e pode exigir matrícula atualizada.',
      recommendation: 'Conferir matrícula, edital e anexos oficiais.'
    },
    {
      title: 'Conservação estimada',
      status: 'Indeterminado',
      description: 'As imagens e dados públicos podem não refletir o estado atual do imóvel.',
      recommendation: 'Considerar vistoria externa e validação da região.'
    },
    {
      title: 'Complexidade jurídica',
      status: 'Requer validação',
      description: 'Leilões podem envolver regras, prazos e condições específicas conforme edital.',
      recommendation: 'Avaliar apoio jurídico quando houver dúvida.'
    },
    {
      title: 'Potencial de reforma',
      status: 'Atenção moderada',
      description: 'Pode haver necessidade de reparos, adequações ou regularizações após a arrematação.',
      recommendation: 'Reservar margem para custos adicionais.'
    },
    {
      title: 'Liquidez estimada',
      status: 'Indeterminado',
      description: 'A demanda depende de preço, localização, documentação, conservação e dinâmica local.'
    }
  ];
  readonly validationChecklist: ValidationChecklistItem[] = [
    { id: 'read-notice', label: 'Li o edital completo' },
    { id: 'checked-registration', label: 'Verifiquei a matrícula do imóvel' },
    { id: 'reviewed-debts', label: 'Analisei possíveis débitos' },
    { id: 'reviewed-extra-costs', label: 'Consultei custos adicionais' },
    { id: 'validated-region', label: 'Validei condições da região' },
    { id: 'reviewed-renovation', label: 'Avaliei necessidade de reforma' },
    { id: 'checked-occupation', label: 'Verifiquei possibilidade de ocupação' },
    { id: 'consulted-support', label: 'Consultei apoio jurídico, se necessário' }
  ];
  readonly estimatedCosts: EstimatedCost[] = [
    { title: 'ITBI', description: 'Pode incidir conforme município, modalidade e momento de transferência.' },
    { title: 'Cartório', description: 'Custos cartoriais podem variar conforme a região e situação documental do imóvel.' },
    { title: 'Reforma', description: 'Reparos e adequações podem ser necessários após validação do estado real do imóvel.' },
    { title: 'Regularização', description: 'Pendências documentais ou cadastrais podem demandar etapas adicionais.' },
    { title: 'Honorários jurídicos', description: 'Apoio especializado pode ser útil quando houver dúvidas sobre edital ou ocupação.' },
    { title: 'IPTU', description: 'Débitos e regras de responsabilidade devem ser conferidos nos documentos oficiais.' },
    { title: 'Condomínio', description: 'Em imóveis condominiais, taxas em aberto devem ser verificadas com atenção.' },
    { title: 'Desocupação', description: 'Quando houver ocupação, podem existir prazos, custos e procedimentos específicos.' }
  ];
  readonly recommendations: RiskRecommendation[] = [
    { title: 'Validar presencialmente a região', description: 'Observe acesso, entorno, ruído, conservação da rua e dinâmica local em diferentes horários.' },
    { title: 'Ler integralmente o edital', description: 'Confirme prazos, regras de pagamento, responsabilidades e condições específicas do leilão.' },
    { title: 'Confirmar situação de ocupação', description: 'Busque sinais atualizados e valide informações antes de considerar qualquer decisão.' },
    { title: 'Consultar matrícula atualizada', description: 'A matrícula pode indicar registros, ônus e informações relevantes para análise documental.' },
    { title: 'Avaliar necessidade de apoio jurídico', description: 'Quando houver dúvidas sobre documentos ou regras, suporte especializado pode ajudar na leitura.' },
    { title: 'Revisar custos totais da operação', description: 'Considere custos além do lance, incluindo taxas, regularização, deslocamentos e eventuais reparos.' }
  ];
  checkedValidationItems: string[] = [];
  isLogged: Observable<boolean>;
  private destroy$ = new Subject<void>();
  private isDestroyed = false;

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private realEstateService: RealEstateService,
              private favoritesService: FavoritesService,
              private marketingConsentService: MarketingConsentService,
              private reportService: ReportService,
              private authenticationService: AuthenticationService,
              private cdr: ChangeDetectorRef) {
    this.favorites = this.favoritesService.favoritesObservable;
    this.isLogged = this.authenticationService.isLogged;
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(async params => {
      const id = params['id'];
      this.isLoading = true;
      this.loadRealEstate(id);
    });
  }


  private initializeReportValues() {
    console.log('initializeReportValues called');

    const prices = [
      this.realEstate?.auction_1_price,
      this.realEstate?.auction_2_price,
      this.realEstate?.minimum_selling_price
    ].filter(p => p !== null && p !== undefined && p !== 0) as number[];

    const minPrice = (prices.length > 0 ? Math.min(...prices) : 0) * 100;
    const assessmentPrice = (this.realEstate?.assessment_price || 0) * 100;

    if (this.realEstate?.report?.['financial_report']) {
      console.log('Financial report found in realEstate data');
      this.costReport = { ...this.realEstate.report['financial_report'] };
      console.log('costReport assigned:', this.costReport);

      // Fill purchase price if empty
      if (minPrice && this.costReport.purchase && !this.costReport.purchase.price_brl_cents) {
        this.costReport.purchase.price_brl_cents = minPrice;
      }

      // Fill sale_projection.sale_price_gross_brl_cents with the assessment price if it's empty
      if (assessmentPrice && this.costReport.sale_projection && !this.costReport.sale_projection.sale_price_gross_brl_cents) {
        this.costReport.sale_projection.sale_price_gross_brl_cents = assessmentPrice;
        console.log('Setting sale_price_gross_brl_cents to assessment price:', this.costReport.sale_projection.sale_price_gross_brl_cents);
      }
    } else {
      console.log('Financial report NOT found in realEstate data - Initializing default');
      this.costReport = {
        schema_version: 'cost_report_v1',
        computed: {
          auctioneer_fee_effective_brl_cents: 0,
          purchase_subtotal_brl_cents: 0,
          total_cost_brl_cents: 0,
          profit_brl_cents: 0,
          roi: 0
        },
        purchase: {
          auction_type: this.realEstate?.auction_type || null,
          sale_type: this.realEstate?.sale_type || null,
          selected_price_basis: null,
          price_brl_cents: minPrice,
          auctioneer_fee_pct: 0.05,
          other_fees_brl_cents: 0
        },
        taxes_and_cartorio: {
          itbi_pct: 0.04,
          registry_brl_cents: 0,
          deed_brl_cents: 0
        },
        sale_projection: {
          sale_price_gross_brl_cents: assessmentPrice,
          brokerage_pct: 0,
          sale_costs_brl_cents: 0,
          taxes_on_gain_brl_cents: 0,
          sale_price_net_brl_cents: 0
        },
        editable_fields: [],
        tables: [],
        missing_fields: [],
        assumptions: [],
        html_report: null
      };
    }
  }

  private loadRealEstate(id: string) {
    this.realEstateService.getRealEstate(id, true).subscribe({
      next: (data: RealEstate) => {
        this.setRealEstateData(data);
      },
      error: (err) => {
        console.error('Error loading real estate details:', err);
        this.isLoading = false;
      }
    });
  }

  private loadPreviewRealEstate(id: string): void {
    let decodedId = '';

    try {
      decodedId = Base64Helper.decode(id).split('#preview-')[0];
    } catch {
      decodedId = '';
    }

    this.realEstateService.getExampleRealEstates().subscribe({
      next: (realEstates) => {
        const matchedRealEstate = realEstates.find((realEstate) => realEstate.link === decodedId) ?? realEstates[0];

        if (matchedRealEstate) {
          this.setRealEstateData(matchedRealEstate);
          return;
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading preview real estate details:', error);
        this.isLoading = false;
      }
    });
  }

  private setRealEstateData(data: RealEstate): void {
    if (data.images && typeof data.images === 'string') {
      data.images = data.images.split(',') as string[];
    }
    this.realEstate = data;
    console.log('Real estate data loaded:', this.realEstate);
    console.log('Documents raw:', data.documents);
    console.log('Documents raw type:', typeof data.documents);
    console.log('Documents is array:', Array.isArray(data.documents));

    if (data.documents && typeof data.documents === 'object' && !Array.isArray(data.documents)) {
      this.realEstate.documents = { ...data.documents };

      for (const key in this.realEstate.documents) {
        if (this.realEstate.documents.hasOwnProperty(key)) {
          this.realEstate.documents[key] = this.realEstate.documents[key].trim();
        }
      }
    }
    console.log('Documents processed:', this.realEstate?.documents);

    this.initializeReportValues();
    this.loadValidationChecklistProgress();

    this.isLoading = false;
    this.cdr.detectChanges();

    this.triggerFlowbiteInit(100, () => {
      // Set default active tab
      // this.switchTab('official-auction-information');
    });
  }

  generateReport() {
    if (this.realEstate?.link) {
      console.log('Generating report for real estate:', this.realEstate.link);
      const id = Base64Helper.encode(this.realEstate.link);
      console.log('Generating report for real estate:', id);
      this.reportService.getReport(id).subscribe({
        next: (report) => {
          this.isLoading = false;
          this.router.navigate(['/propriedade', id, 'relatorio']);
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Error generating report:', err);
        }
      });

      this.router.navigate(['/propriedade', id, 'relatorio']);
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private triggerFlowbiteInit(delay: number = 1, callback?: () => void) {
    setTimeout(() => {
      if (!this.isDestroyed) {
        initFlowbite();
        if (callback) {
          callback();
        }
      }
    }, delay);
  }

  async includeFavorite(realEstateId: string): Promise<void> {
    const currentUser = await this.authenticationService.getCurrentUser();
    if (!currentUser) {
      this.openAuthChoiceModal();
      return;
    }

    try {
      const hasRecordedAlertDecision = sessionStorage.getItem('casato-real-estate-alerts-decision') === 'true'
        || await this.marketingConsentService.hasRecordedRealEstateAlertsDecision();

      if (hasRecordedAlertDecision) {
        this.favoritesService.includeFavorite(realEstateId);
        return;
      }
    } catch (error) {
      console.error('Failed to check favorite alert consent:', error);
    }

    this.openFavoriteAlertConsent(realEstateId);
  }

  private openFavoriteAlertConsent(realEstateId: string): void {
    this.pendingFavoriteRealEstateId = realEstateId;
    this.favoriteAlertConsentError = '';
    this.showFavoriteAlertConsentModal = true;
  }

  async saveFavoriteAlertConsent(receiveAlerts: boolean): Promise<void> {
    if (!this.pendingFavoriteRealEstateId) return;

    this.isSavingFavoriteAlertConsent = true;
    this.favoriteAlertConsentError = '';
    const realEstateId = this.pendingFavoriteRealEstateId;

    try {
      await this.marketingConsentService.saveRealEstateAlerts(receiveAlerts);
    } catch (error) {
      console.error('Failed to save favorite alert consent:', error);
    } finally {
      sessionStorage.setItem('casato-real-estate-alerts-decision', 'true');
      this.favoritesService.includeFavorite(realEstateId);
      this.closeFavoriteAlertConsent();
      this.isSavingFavoriteAlertConsent = false;
    }
  }

  private closeFavoriteAlertConsent(): void {
    this.showFavoriteAlertConsentModal = false;
    this.pendingFavoriteRealEstateId = null;
  }

  openAuthChoiceModal(): void {
    this.showAuthChoiceModal = true;
  }

  closeAuthChoiceModal(): void {
    this.showAuthChoiceModal = false;
  }

  deleteFavorite(realEstateId: string) {
    this.favoritesService.deleteFavorite(realEstateId);
  }

  navigateToDetails(id: string) {
    const encodedId = Base64Helper.encode(id);
    window.open('/propriedade/' + encodedId, '_blank');
  }

  ngAfterViewInit(): void {
    // Initialize Flowbite tabs
    this.triggerFlowbiteInit(200, () => {
      // Set default active tab
      // this.switchTab('official-auction-information');
    });
  }

  // Method to manually switch tabs if needed
  switchTab(tabId: string): void {
    console.log('switchTab called with:', tabId);
    this.activeDetailsTab = tabId;

    this.detailsTabs.forEach((tab) => {
      const tabPanel = document.getElementById(tab.id);
      const tabButton = document.getElementById(tab.buttonId);
      const isActive = tab.id === tabId;

      tabPanel?.classList.toggle('hidden', !isActive);
      tabButton?.classList.toggle('text-blue-600', isActive);
      tabButton?.classList.toggle('border-blue-600', isActive);
      tabButton?.classList.toggle('border-transparent', !isActive);
      tabButton?.classList.toggle('hover:text-gray-600', !isActive);
      tabButton?.classList.toggle('hover:border-gray-300', !isActive);
    });

    if (tabId === 'neighborhood-analysis') {
      setTimeout(() => {
        this.centerNeighborhoodMap();
        this.nearbyPlacesComponent?.centerMap();
      }, 0);
    }
  }

  toggleValidationChecklistItem(itemId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked && !this.checkedValidationItems.includes(itemId)) {
      this.checkedValidationItems = [...this.checkedValidationItems, itemId];
    }

    if (!checked) {
      this.checkedValidationItems = this.checkedValidationItems.filter((id) => id !== itemId);
    }

    this.saveValidationChecklistProgress();
  }

  isValidationChecklistItemChecked(itemId: string): boolean {
    return this.checkedValidationItems.includes(itemId);
  }

  getValidationChecklistCompletedCount(): number {
    return this.checkedValidationItems.length;
  }

  getValidationChecklistProgress(): number {
    if (!this.validationChecklist.length) return 0;
    return Math.round((this.checkedValidationItems.length / this.validationChecklist.length) * 100);
  }

  isValidationChecklistComplete(): boolean {
    return this.checkedValidationItems.length === this.validationChecklist.length;
  }

  private loadValidationChecklistProgress(): void {
    const key = this.getValidationChecklistStorageKey();
    if (!key) return;

    try {
      const stored = localStorage.getItem(key);
      this.checkedValidationItems = stored ? JSON.parse(stored) : [];
    } catch {
      this.checkedValidationItems = [];
    }
  }

  private saveValidationChecklistProgress(): void {
    const key = this.getValidationChecklistStorageKey();
    if (!key) return;

    localStorage.setItem(key, JSON.stringify(this.checkedValidationItems));
  }

  private getValidationChecklistStorageKey(): string | null {
    if (!this.realEstate?.link) return null;
    return `casato-validation-checklist:${this.realEstate.link}`;
  }

  private centerNeighborhoodMap(): void {
    if (!this.realEstate?.lat || !this.realEstate?.lng || !this.neighborhoodMap?.googleMap) {
      return;
    }

    const center = {
      lat: this.realEstate.lat,
      lng: this.realEstate.lng
    };

    this.neighborhoodMap.googleMap.setCenter(center);
    this.neighborhoodMap.googleMap.setZoom(15);
  }

  getAnalysisPointPosition(point: AnalysisMapPoint): { lat: number; lng: number } {
    return {
      lat: (this.realEstate?.lat || 0) + point.coordinates.latOffset,
      lng: (this.realEstate?.lng || 0) + point.coordinates.lngOffset
    };
  }

  getAnalysisPointLabel(point: AnalysisMapPoint): google.maps.MarkerLabel {
    return {
      text: point.label,
      color: '#ffffff',
      fontSize: '12px',
      fontWeight: '600'
    };
  }

  checkPastDate(date: string | undefined): boolean {
      if (!date) return false;

      // Expecting format: %Y-%m-%dT%H:%M:%SZ (e.g., 2025-12-18T07:32:00Z)
      const isoUtcPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
      if (!isoUtcPattern.test(date)) {
        console.warn('Invalid date format (expected %Y-%m-%dT%H:%M:%SZ):', date);
        return false;
      }

      const d = new Date(date);

      if (isNaN(d.getTime())) {
        console.warn('Unable to parse date:', date);
        return false;
      }

      const now = new Date();
      return now.getTime() > d.getTime();
  }

  isAssessmentPriceScratched(): boolean {
    if (!this.realEstate || !this.realEstate.assessment_price) return false;
    const activeAuction = this.getActiveAuctionData();
    if (!activeAuction.price) return false;
    return activeAuction.price < this.realEstate.assessment_price;
  }

  getActiveAuctionData(): { price: number | undefined, discount: number | undefined } {
    if (!this.realEstate) return { price: undefined, discount: undefined };

    const now = new Date().getTime();

    // Check 2nd auction first (if it exists and has dates)
    if (this.realEstate.auction_2_start_date && this.realEstate.auction_2_end_date) {
      const start2 = new Date(this.realEstate.auction_2_start_date).getTime();
      const end2 = new Date(this.realEstate.auction_2_end_date).getTime();

      if (now >= start2 && now <= end2) {
        return {
          price: this.realEstate.auction_2_price,
          discount: this.realEstate.auction_2_discount
        };
      }
    }

    // Check 1st auction
    if (this.realEstate.auction_1_start_date && this.realEstate.auction_1_end_date) {
      const start1 = new Date(this.realEstate.auction_1_start_date).getTime();
      const end1 = new Date(this.realEstate.auction_1_end_date).getTime();

      if (now >= start1 && now <= end1) {
        return {
          price: this.realEstate.auction_1_price,
          discount: this.realEstate.auction_1_discount
        };
      }
    }

    // Fallback if no active auction window or no dates provided for 1st/2nd auctions
    // The requirement says: "when there is two auctions the active value should be determined by the end date and start date"
    // "Other way, the value is minimum_selling_price with discount"

    // If 2nd auction is upcoming, maybe we should show 1st auction price? Or if 1st is in past, show 2nd?
    // Usually "Active value" means what is currently being bid on.
    // If 1st auction is in the past, and 2nd is in the future or present, 2nd is usually the target.

    if (this.realEstate.auction_2_start_date) {
      const end1 = this.realEstate.auction_1_end_date ? new Date(this.realEstate.auction_1_end_date).getTime() : 0;
      if (now > end1) {
          return {
            price: this.realEstate.auction_2_price,
            discount: this.realEstate.auction_2_discount
          };
      }
    }

    if (this.realEstate.auction_1_start_date) {
        return {
          price: this.realEstate.auction_1_price,
          discount: this.realEstate.auction_1_discount
        };
    }

    // Default fallback
    return {
      price: this.realEstate.minimum_selling_price,
      discount: this.realEstate.discount
    };
  }
}
